import React, { FC, memo, useCallback, useRef, useState } from 'react'
import { Box, FormControl, Input, InputGroup, Text, VStack } from '@chakra-ui/react'
import { Button, Icon } from '../../components'
import { useTranslation } from 'react-i18next'
import { updateCookieProvince } from '../../scenarios/utils'

interface PostalCodeProps {
  onSubmit?: (postalCode: string) => void
  name?: string
  placeholder?: string | null
}

const VALID_CANADIAN_POSTAL_CODE_REGEX =
  /^([ABCEGHJ-NPRSTVXY]([0-9]([ABCEGHJ-NPRSTV-Z]([ ]?([0-9]([ABCEGHJ-NPRSTV-Z]([0-9])?)?)?)?)?)?)$/i

export const PostalCode: FC<PostalCodeProps> = memo(({ onSubmit, name, placeholder = null }) => {
  const postalCodeInputElement = useRef<HTMLInputElement>(null)
  const [isValid, setIsValid] = useState(false)
  const [isDirty, setIsDirty] = useState(false)
  const [isPostalCodeComplete, setIsPostalCodeComplete] = useState(false)
  const [formattedPostalCode, setFormattedPostalCode] = useState<string>('')
  const [previousFormattedLength, setPreviousFormattedLength] = useState<number>(0)
  const { t } = useTranslation()

  const isValidPostalCode = useCallback((rawPostalCode: string): boolean => {
    const matches = rawPostalCode.match(VALID_CANADIAN_POSTAL_CODE_REGEX)

    return matches !== null && matches.length > 0
  }, [])

  const validatePostalCode = useCallback(
    (postalCode: string) => {
      const rawPostalCode = postalCode.replace(/ /, '')

      const valid = isValidPostalCode(rawPostalCode)

      setIsValid(valid)
      setIsPostalCodeComplete(valid && rawPostalCode.length === 6)
    },
    [isValidPostalCode, setIsValid, setIsPostalCodeComplete],
  )

  const getCaretPosition = useCallback((): number => {
    if (!postalCodeInputElement.current) {
      return 0
    }

    return postalCodeInputElement.current.selectionStart || 0
  }, [postalCodeInputElement])

  const setCaretPosition = useCallback(
    (caretPos: number) => {
      window.requestAnimationFrame(() => {
        postalCodeInputElement.current?.setSelectionRange(caretPos, caretPos)
      })
    },
    [postalCodeInputElement],
  )

  const formatPostalCode = useCallback(
    (postalCode: string) => {
      let cursorPos = getCaretPosition()
      let partiallyFormattedPostalCode = postalCode.toUpperCase()
      partiallyFormattedPostalCode = partiallyFormattedPostalCode.trimStart().replace(/\s*/g, '')
      if (partiallyFormattedPostalCode.length > 3) {
        partiallyFormattedPostalCode =
          partiallyFormattedPostalCode.substr(0, 3) + ' ' + partiallyFormattedPostalCode.substr(3)
        partiallyFormattedPostalCode = partiallyFormattedPostalCode.replace(/\s{2,}/g, ' ')
      }

      if (previousFormattedLength !== 0) {
        if (previousFormattedLength <= partiallyFormattedPostalCode.length && cursorPos === 4) {
          cursorPos++
        }
      } else if (cursorPos !== 0) {
        cursorPos = partiallyFormattedPostalCode.length
      }
      setFormattedPostalCode(partiallyFormattedPostalCode)
      setPreviousFormattedLength(partiallyFormattedPostalCode.length)
      setCaretPosition(cursorPos)
    },
    [previousFormattedLength, setFormattedPostalCode, setPreviousFormattedLength, getCaretPosition, setCaretPosition],
  )

  const onPostalCodeChange = useCallback(() => {
    const postalCode = postalCodeInputElement.current?.value || ''

    setIsDirty(postalCode !== '')
    formatPostalCode(postalCode)
    validatePostalCode(postalCode)
  }, [setIsDirty, formatPostalCode, validatePostalCode])

  const invalidPostalCodeMessage = t('error_invalid-postal-code')

  return (
    <Box>
      <VStack spacing={3}>
        <Box border={2} borderStyle="solid" borderColor="grey" borderRadius="md">
          <FormControl>
            <InputGroup outline="none" borderRadius="md">
              <Input
                ref={postalCodeInputElement}
                outline="none"
                /*
                 * This is a workaround only when using nuka-carousel based on this comment:
                 * https://github.com/FormidableLabs/nuka-carousel/issues/694#issuecomment-944043125
                 *
                 * It basically lets us click the input field within the carousel. If we need more
                 * than one input field in the future, we need to consider wrapping the Chakra input
                 * field into a custom component
                 */
                name={name}
                onClick={(e) => {
                  e.stopPropagation()
                  e.currentTarget.focus()
                }}
                onChange={() => onPostalCodeChange()}
                variant="default"
                aria-label={placeholder ? placeholder : t('postal-code_placeholder')}
                height={85}
                maxWidth={450}
                fontSize={22}
                value={formattedPostalCode}
                placeholder={placeholder ? placeholder : t('postal-code_placeholder')}
                maxLength={7}
                autoFocus
                onKeyPress={(ev) => {
                  if (isPostalCodeComplete && ev.key === 'Enter') {
                    ev.preventDefault()
                    updateCookieProvince(formattedPostalCode)
                    onSubmit && onSubmit(formattedPostalCode)
                  }
                }}
                backgroundColor="transparent"
                _placeholder={{ color: 'green.500' }}
              />
              {isPostalCodeComplete && (
                <Button
                  role={'button'}
                  title={'button submit'}
                  width={20}
                  size="2xl"
                  backgroundColor="green.500"
                  variant="solid"
                  borderRadius="0% 5% 5% 0%"
                  _hover={{
                    backgroundColor: 'green.500',
                  }}
                  _focus={{
                    borderColor: 'brand',
                    boxShadow: '0 0 0 2px #5bce4c',
                  }}
                  tabIndex={0}
                  onClick={() => {
                    updateCookieProvince(formattedPostalCode)
                    onSubmit && onSubmit(formattedPostalCode)
                  }}
                >
                  <Icon icon="arrow-right" color="white" />
                </Button>
              )}
            </InputGroup>
          </FormControl>
        </Box>
      </VStack>
      {isDirty && !isValid && (
        <Text id="errorMessage" color={'red'} fontSize="xs">
          {invalidPostalCodeMessage}
        </Text>
      )}
    </Box>
  )
})
